import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { api } from "@webcore/services/api/api";
import { containerToTags } from "@webcore/services/signalr/containerTagMapping";
import { bumpDataChangeVersions } from "@webcore/services/signalr/dataChangeVersions";
import { clientInstanceId } from "@webcore/services/clientInstanceId";

const DEBOUNCE_MS = 1500;

/**
 * Returns a handler that maps a data-change notification (Cosmos container name + the client
 * instance that originated the change) to the matching RTK Query cache tags and invalidates them,
 * debounced. Echoes of this client's own writes are ignored via the originating instance id.
 *
 * Shared by the direct SignalR subscription ({@link useSignalR}) and the parent→iframe forwarded
 * channel (the MFE data-change listener) so both feed the exact same invalidation logic.
 */
export function useDataChangeInvalidator(): (containerName: string, sourceClientInstanceId: string | null) => void {
  const dispatch = useDispatch();
  const debounceTimerRef = useRef<number | null>(null);
  const pendingTagsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    return () => {
      pendingTagsRef.current.clear();
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  return useCallback((containerName: string, sourceClientInstanceId: string | null) => {
    if (sourceClientInstanceId === clientInstanceId) return;

    const tags = containerToTags[containerName];
    if (tags) {
      tags.forEach(t => pendingTagsRef.current.add(t));
    }

    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = window.setTimeout(() => {
      const tagsToInvalidate = [...pendingTagsRef.current];
      pendingTagsRef.current.clear();
      debounceTimerRef.current = null;
      dispatch(api.util.invalidateTags(tagsToInvalidate as Parameters<typeof api.util.invalidateTags>[0]));
      // Consumers that fetch outside the query cache (custom dashboard view code) refresh off these
      // counters rather than off the tag invalidation.
      bumpDataChangeVersions(tagsToInvalidate);
    }, DEBOUNCE_MS);
  }, [dispatch]);
}
